<template>
	<div>
	<v-dialog v-model="dialogOpen" max-width="800" persistent scrollable>
		<v-card class="viagem-dialog" outlined>
			<v-card-title class="viagem-dialog__title">
				<span>{{ dialogTitle }}</span>
				<v-spacer />
				<v-btn icon :disabled="saving" @click="fecharDialog">
					<v-icon>mdi-close</v-icon>
				</v-btn>
			</v-card-title>

			<v-divider />

			<v-card-text class="pt-6 pb-0">
				<v-form ref="cadastroForm" v-model="formValid" lazy-validation>
					<div class="viagem-section-title mt-0">
						<v-icon style="font-size: 18px;">mdi-format-list-text</v-icon> 
						Informacoes basicas
					</div>
					<v-divider class="mb-4" />

					<v-row dense class="pl-4">
						<v-col cols="12" md="6">
							<v-text-field
								v-model.trim="form.codigo_carga"
								label="Codigo da Carga"
								outlined
								dense
								:rules="[rules.required]"
							/>
						</v-col>

						<v-col cols="12" md="6">
							<v-text-field
								v-model="form.peso"
								label="Peso"
								type="text"
								inputmode="numeric"
								outlined
								dense
								:rules="[rules.integerValue]"
								@input="onPesoInput"
							/>
						</v-col>

						<v-col cols="12" md="6">
							<v-text-field
								v-model="form.km_inicial"
								label="KM Inicial"
								type="text"
								inputmode="numeric"
								outlined
								dense
								suffix="km"
								:rules="[rules.integerValue]"
								@input="onKmInicialInput"
							/>
						</v-col>

						<v-col cols="12" md="6">
							<v-text-field
								v-model="form.valor_frete"
								label="Frete Bruto"
								outlined
								dense
								prefix="R$"
								:rules="[rules.required, rules.currency]"
								@input="onCurrencyInput('valor_frete')"
							/>
						</v-col>

						<v-col cols="12" md="6">
							<v-text-field
								v-model="form.imposto"
								label="Imposto (%)"
								outlined
								dense
								inputmode="decimal"
								suffix="%"
								:rules="[rules.percent]"
								@input="onPercentInput('imposto')"
							/>
						</v-col>

						<v-col cols="12" md="6">
							<v-text-field
								:value="freteLiquidoFormatado"
								label="Frete Liquido"
								outlined
								dense
								readonly
								prefix="R$"
							/>
						</v-col>

						<v-col cols="12" md="6">
							<v-autocomplete
								v-model="form.contratante_id"
								:items="contratantes"
								:loading="loadingContratantes"
								label="Contratante"
								item-text="label"
								item-value="id"
								outlined
								dense
								clearable
								no-data-text="Nenhum contratante encontrado"
								:filter="filtrarContratantes"
							/>
						</v-col>

						<v-col cols="12" md="6">
							<v-autocomplete
								v-model="form.motorista_id"
								:items="motoristas"
								:loading="loadingMotoristas"
								label="Motorista"
								item-text="nome"
								item-value="id"
								outlined
								dense
								clearable
								no-data-text="Nenhum motorista encontrado"
								:rules="[rules.required]"
							/>
						</v-col>

						<v-col cols="12" md="6">
							<v-autocomplete
								v-model="form.veiculo_id"
								:items="veiculos"
								:loading="loadingVeiculos"
								label="Veiculo"
								item-text="placa"
								item-value="id"
								outlined
								dense
								clearable
								no-data-text="Nenhum veiculo encontrado"
								:rules="[rules.required]"
							/>
						</v-col>

						<v-col cols="12" md="6">
							<v-text-field
								v-model="form.data_saida"
								label="Data de saida"
								type="date"
								outlined
								dense
							/>
						</v-col>

						<v-col cols="12" md="6">
							<v-text-field
								v-model="form.previsao_chegada"
								label="Previsao de chegada"
								type="date"
								outlined
								dense
							/>
						</v-col>

						<v-col cols="12" md="6">
							<v-select
								v-model="form.tipo_operacao"
								:items="tiposOperacao"
								label="Tipo da operacao"
								outlined
								dense
							/>
						</v-col>

						<v-col cols="12" md="6">
							<v-select
								v-model="form.status_viagem"
								:items="statusViagem"
								label="Status da viagem"
								outlined
								dense
								:rules="[rules.required]"
							/>
						</v-col>

						<v-col cols="12">
							<v-textarea
								v-model.trim="form.observacoes"
								label="Observacoes"
								outlined
								dense
								rows="3"
								auto-grow
							/>
						</v-col>
					</v-row>

					<div class="viagem-section-title mt-6"> 
						<v-icon style="font-size: 18px;">mdi-map-marker-distance</v-icon> 
						Paradas
					</div>
					<v-divider class="mb-4" />

					<div
						v-for="(parada, index) in form.paradas"
						:key="`parada-${index}`"
						class="parada-box mb-4 ml-4"
					>
						<div class="parada-box__header">
							<span style="color: #8b8b8b"><strong>Parada {{ index + 1 }}</strong></span>
							<v-btn
								icon
								small
								:disabled="form.paradas.length === 1"
								@click="removerParada(index)"
							>
								<v-icon color="error">mdi-delete-outline</v-icon>
							</v-btn>
						</div>

						<v-row dense>
							<v-col cols="12" md="6">
								<v-autocomplete
									v-model="parada.cidade_uf"
									label="Cidade - UF"
									:items="municipios"
									:loading="loadingMunicipios"
									item-text="label"
									item-value="label"
									clearable
									no-data-text="Nenhum municipio encontrado"
									hide-selected
									cache-items
									:menu-props="{ maxHeight: 320 }"
									:filter="filtrarMunicipios"
									outlined
									dense
								/>
							</v-col>

							<v-col cols="12" md="6">
								<v-select
									v-model="parada.tipo"
									:items="tiposParada"
									label="Tipo"
									outlined
									dense
								/>
							</v-col>

							<v-col cols="12" md="6">
								<v-text-field
									v-model.trim="parada.cliente"
									label="Cliente"
									outlined
									dense
								/>
							</v-col>

							<v-col cols="12" md="6">
								<v-text-field
									v-model.trim="parada.observacoes"
									label="Observacoes"
									outlined
									dense
								/>
							</v-col>
						</v-row>
					</div>

					<v-btn class="viagem-add-stop-btn" text @click="adicionarParada">
						<v-icon left size="18">mdi-plus-circle-outline</v-icon>
						Adicionar parada
					</v-btn>
				</v-form>
			</v-card-text>

			<v-card-actions class="px-6 pb-6 pt-3">
				<v-spacer />
				<v-btn text :disabled="saving" @click="fecharDialog">Cancelar</v-btn>
				<v-btn class="viagem-save-btn" depressed :loading="saving" :disabled="saving" @click="salvarViagem">
					{{ saveButtonLabel }}
				</v-btn>
			</v-card-actions>
		</v-card>
	</v-dialog>
	</div>
</template>

<script>
import contratantesServices from './contratantesServices';
import MotoristaServices from '../motoristas/motoristaServices';
import VeiculoServices from '../veiculos/veiculoServices';
import viagensServices from './viagensServices';
import { listarMunicipiosBrasil } from './municipiosBrasil';

const createParada = () => ({
	cidade_uf: '',
	tipo: null,
	cliente: '',
	observacoes: '',
});

const createInitialForm = () => ({
	codigo_carga: '',
	peso: '',
	km_inicial: '',
	valor_frete: '',
	imposto: '',
	contratante_id: null,
	motorista_id: null,
	veiculo_id: null,
	data_saida: '',
	previsao_chegada: '',
	tipo_operacao: null,
	status_viagem: 'Pendente',
	observacoes: '',
	paradas: [createParada()],
});

export default {
	name: 'CadastroViagemDialog',

	props: {
		value: {
			type: Boolean,
			default: false,
		},
		viagem: {
			type: Object,
			default: null,
		},
	},

	data() {
		return {
			dialogOpen: false,
			formValid: false,
			saving: false,
			loadingContratantes: false,
			loadingMotoristas: false,
			loadingVeiculos: false,
			loadingMunicipios: false,
			contratantes: [],
			motoristas: [],
			veiculos: [],
			municipios: [],
			form: createInitialForm(),
			tiposOperacao: [
				'Entrega simples',
				'Coleta',
				'Coleta + entrega',
				'Transferencia entre filiais',
				'Viagem com multiplas entregas',
			],
			statusViagem: [
				'Pendente',
				'Em carregamento',
				'Em Transito',
				'Aguardando Descarga',
				'Finalizada',
				'Cancelada',
			],
			tiposParada: ['Carrega', 'Entrega', 'Retorna'],
			rules: {
				required: (v) => Boolean(v || v === 0) || 'Campo obrigatorio',
				integerValue: (v) => {
					if (!v) {
						return true;
					}

					const numericValue = this.parseInteger(v);
					return Number.isInteger(numericValue) && numericValue >= 0 || 'Informe um valor valido';
				},
				currency: (v) => {
					if (!v) {
						return true;
					}

					const numericValue = this.parseCurrency(v);
					return Number.isFinite(numericValue) && numericValue >= 0 || 'Informe um valor valido';
				},
				percent: (v) => {
					if (!v) {
						return true;
					}

					const numericValue = this.parsePercent(v);
					return Number.isFinite(numericValue) && numericValue >= 0 && numericValue <= 100 || 'Informe um percentual entre 0 e 100';
				},
			},
		};
	},

	computed: {
		isEditMode() {
			return Boolean(this.viagem && this.viagem.id);
		},

		dialogTitle() {
			return this.isEditMode ? 'Editar Viagem' : 'Cadastro de Viagem';
		},

		saveButtonLabel() {
			return this.isEditMode ? 'Salvar alteracoes' : 'Salvar viagem';
		},

		freteLiquidoFormatado() {
			if (!this.form.valor_frete) {
				return '';
			}

			const freteBruto = this.parseCurrency(this.form.valor_frete);
			const impostoPercentual = this.parsePercent(this.form.imposto);
			const freteLiquido = this.calcularFreteLiquido(freteBruto, impostoPercentual);

			return this.formatCurrencyNumber(freteLiquido);
		},
	},

	watch: {
		value: {
			immediate: true,
			handler(newValue) {
				this.dialogOpen = newValue;
			},
		},

		dialogOpen(newValue) {
			this.$emit('input', newValue);
			if (newValue) {
				this.carregarDadosDeSelecao();
				this.preencherFormulario();
				return;
			}

			if (!this.saving) {
				this.resetForm();
			}
		},

		viagem: {
			deep: true,
			handler() {
				if (this.dialogOpen) {
					this.preencherFormulario();
				}
			}
		},
	},

	methods: {
		async carregarDadosDeSelecao() {
			await Promise.all([
				this.buscarContratantes(),
				this.buscarMotoristas(),
				this.buscarVeiculos(),
				this.buscarMunicipios(),
			]);
		},

		formatCnpj(value) {
			const digitsOnly = String(value || '').replace(/\D/g, '').slice(0, 14);
			if (!digitsOnly) {
				return '';
			}

			return digitsOnly
				.replace(/^(\d{2})(\d)/, '$1.$2')
				.replace(/^(\d{2})\.(\d{3})(\d)/, '$1.$2.$3')
				.replace(/\.(\d{3})(\d)/, '.$1/$2')
				.replace(/(\/\d{4})(\d)/, '$1-$2');
		},

		buildContratanteLabel(item) {
			if (!item) {
				return '-';
			}

			return item.cnpj ? `${item.nome} - ${this.formatCnpj(item.cnpj)}` : item.nome;
		},

		async buscarContratantes() {
			this.loadingContratantes = true;
			try {
				const response = await contratantesServices.listar();
				const data = Array.isArray(response.data) ? response.data : [];
				this.contratantes = data.map((item) => ({
					id: item.id,
					nome: item.nome || '-',
					cnpj: item.cnpj || null,
					label: this.buildContratanteLabel(item),
				}));
			} catch (error) {
				this.contratantes = [];
			}
			this.loadingContratantes = false;
		},

		async buscarMunicipios() {
			this.loadingMunicipios = true;
			try {
				this.municipios = await listarMunicipiosBrasil();
			} catch (error) {
				this.municipios = [];
				this.$toasted?.show('Nao foi possivel carregar a lista de municipios');
			}
			this.loadingMunicipios = false;
		},

		async buscarMotoristas() {
			this.loadingMotoristas = true;
			try {
				const response = await MotoristaServices.listar();
				const data = Array.isArray(response.data) ? response.data : [];
				this.motoristas = data.map((item) => ({
					id: item.id,
					nome: item.nome || '-',
				}));
			} catch (error) {
				this.motoristas = [];
			}
			this.loadingMotoristas = false;
		},

		async buscarVeiculos() {
			this.loadingVeiculos = true;
			try {
				const response = await VeiculoServices.listar();
				const data = Array.isArray(response.data) ? response.data : [];
				this.veiculos = data.map((item) => ({
					id: item.id,
					placa: item.placa || '-',
				}));
			} catch (error) {
				this.veiculos = [];
			}
			this.loadingVeiculos = false;
		},

		adicionarParada() {
			this.form.paradas.push(createParada());
		},

		formatInteger(value) {
			const digitsOnly = String(value || '').replace(/\D/g, '');
			if (!digitsOnly) {
				return '';
			}

			const integerValue = Number(digitsOnly);
			return integerValue.toLocaleString('pt-BR', {
				maximumFractionDigits: 0,
			});
		},

		parseInteger(value) {
			if (!value) {
				return 0;
			}

			const normalized = String(value).replace(/\D/g, '');
			const parsed = Number(normalized);
			return Number.isFinite(parsed) ? parsed : 0;
		},

		formatCurrency(value) {
			const digitsOnly = String(value || '').replace(/\D/g, '');
			if (!digitsOnly) {
				return '';
			}

			const centsValue = Number(digitsOnly) / 100;
			return centsValue.toLocaleString('pt-BR', {
				minimumFractionDigits: 2,
				maximumFractionDigits: 2,
			});
		},

		formatCurrencyNumber(value) {
			const numericValue = Number(value);
			if (!Number.isFinite(numericValue)) {
				return '';
			}

			return numericValue.toLocaleString('pt-BR', {
				minimumFractionDigits: 2,
				maximumFractionDigits: 2,
			});
		},

		parseCurrency(value) {
			if (!value) {
				return 0;
			}

			const normalized = String(value)
				.replace(/\./g, '')
				.replace(',', '.');

			const parsed = Number(normalized);
			return Number.isFinite(parsed) ? parsed : 0;
		},

		formatPercent(value) {
			const normalized = String(value || '')
				.replace(/\./g, ',')
				.replace(/[^\d,]/g, '');

			if (!normalized) {
				return '';
			}

			const commaIndex = normalized.indexOf(',');
			const integerPart = commaIndex >= 0 ? normalized.slice(0, commaIndex) : normalized;
			const decimalPart = commaIndex >= 0
				? normalized.slice(commaIndex + 1).replace(/,/g, '').slice(0, 2)
				: '';
			const cleanInteger = integerPart.replace(/^0+(?=\d)/, '') || '0';

			return commaIndex >= 0 ? `${cleanInteger},${decimalPart}` : cleanInteger;
		},

		parsePercent(value) {
			if (!value) {
				return 0;
			}

			const normalized = String(value)
				.replace(/\./g, '')
				.replace(',', '.');
			const parsed = Number(normalized);

			return Number.isFinite(parsed) ? parsed : 0;
		},

		calcularFreteLiquido(freteBruto, impostoPercentual) {
			const bruto = Number(freteBruto || 0);
			const percentual = Number(impostoPercentual || 0);

			if (!Number.isFinite(bruto) || !Number.isFinite(percentual)) {
				return 0;
			}

			return bruto - (bruto * percentual / 100);
		},

		formatDateInput(value) {
			if (!value) {
				return '';
			}

			const rawValue = String(value).trim();
			if (/^\d{4}-\d{2}-\d{2}/.test(rawValue)) {
				return rawValue.slice(0, 10);
			}

			const date = new Date(value);
			if (Number.isNaN(date.getTime())) {
				return '';
			}

			const year = date.getFullYear();
			const month = String(date.getMonth() + 1).padStart(2, '0');
			const day = String(date.getDate()).padStart(2, '0');
			return `${year}-${month}-${day}`;
		},

		preencherFormulario() {
			if (!this.isEditMode) {
				this.resetForm();
				return;
			}

			const paradas = Array.isArray(this.viagem?.paradas) && this.viagem.paradas.length > 0
				? this.viagem.paradas.map((parada) => ({
					cidade_uf: parada.cidade_uf || '',
					tipo: parada.tipo || null,
					cliente: parada.cliente || '',
					observacoes: parada.observacoes || '',
				}))
				: [createParada()];

			this.form = {
				codigo_carga: this.viagem?.codigo_carga || '',
				peso: this.viagem?.peso ? this.formatInteger(this.viagem.peso) : '',
				km_inicial: this.viagem?.km_inicial !== null && this.viagem?.km_inicial !== undefined
					? this.formatInteger(this.viagem.km_inicial)
					: '',
				valor_frete: this.viagem?.valor_frete !== null && this.viagem?.valor_frete !== undefined
					? this.formatCurrency(this.viagem.valor_frete)
					: '',
				imposto: this.viagem?.imposto !== null && this.viagem?.imposto !== undefined
					? this.formatPercent(this.viagem.imposto)
					: '',
				contratante_id: this.viagem?.contratante_id || null,
				motorista_id: this.viagem?.motorista_id || null,
				veiculo_id: this.viagem?.veiculo_id || null,
				data_saida: this.formatDateInput(this.viagem?.data_saida),
				previsao_chegada: this.formatDateInput(this.viagem?.previsao_chegada),
				tipo_operacao: this.viagem?.tipo_operacao || null,
				status_viagem: this.viagem?.status || 'Pendente',
				observacoes: this.viagem?.observacoes || '',
				paradas,
			};

			this.formValid = false;
			this.$nextTick(() => {
				this.$refs?.cadastroForm?.resetValidation?.();
			});
		},

		onCurrencyInput(fieldName) {
			this.form[fieldName] = this.formatCurrency(this.form[fieldName]);
		},

		onPercentInput(fieldName) {
			this.form[fieldName] = this.formatPercent(this.form[fieldName]);
		},

		normalizeText(value) {
			return String(value || '')
				.normalize('NFD')
				.replace(/[\u0300-\u036f]/g, '')
				.toLowerCase()
				.trim();
		},

		filtrarMunicipios(item, queryText, itemText) {
			if (!queryText) {
				return true;
			}

			const termoBusca = this.normalizeText(queryText);
			const textoItem = this.normalizeText(itemText || item?.label || '');
			const nomeItem = this.normalizeText(item?.nome || '');
			const ufItem = this.normalizeText(item?.uf || '');

			return textoItem.includes(termoBusca)
				|| nomeItem.includes(termoBusca)
				|| ufItem.includes(termoBusca);
		},

		filtrarContratantes(item, queryText, itemText) {
			if (!queryText) {
				return true;
			}

			const termoBusca = this.normalizeText(queryText);
			const textoItem = this.normalizeText(itemText || item?.label || '');
			const nomeItem = this.normalizeText(item?.nome || '');
			const cnpjItem = String(item?.cnpj || '');
			const termoNumerico = String(queryText || '').replace(/\D/g, '');

			return textoItem.includes(termoBusca)
				|| nomeItem.includes(termoBusca)
				|| (termoNumerico ? cnpjItem.includes(termoNumerico) : false);
		},

		onPesoInput() {
			this.form.peso = this.formatInteger(this.form.peso);
		},

		onKmInicialInput() {
			this.form.km_inicial = this.formatInteger(this.form.km_inicial);
		},

		removerParada(index) {
			if (this.form.paradas.length === 1) {
				return;
			}

			this.form.paradas.splice(index, 1);
		},

		fecharDialog(force = false) {
			if (this.saving && !force) {
				return;
			}

			this.dialogOpen = false;
			this.resetForm();
		},

		resetForm() {
			this.form = createInitialForm();
			this.formValid = false;
			this.$nextTick(() => {
				this.$refs?.cadastroForm?.resetValidation?.();
			});
		},

		async salvarViagem() {
			const isValid = this.$refs?.cadastroForm?.validate?.();
			if (!isValid) {
				return;
			}

			this.saving = true;

			try {
				const payload = {
					codigo_carga: this.form.codigo_carga,
					peso: this.form.peso ? this.parseInteger(this.form.peso) : null,
					km_inicial: this.form.km_inicial ? this.parseInteger(this.form.km_inicial) : null,
					valor_frete: this.parseCurrency(this.form.valor_frete),
					imposto: this.form.imposto ? this.parsePercent(this.form.imposto) : null,
					contratante_id: this.form.contratante_id,
					motorista_id: this.form.motorista_id,
					veiculo_id: this.form.veiculo_id,
					data_saida: this.form.data_saida || null,
					previsao_chegada: this.form.previsao_chegada || null,
					tipo_operacao: this.form.tipo_operacao,
					status: this.form.status_viagem,
					observacoes: this.form.observacoes || null,
					paradas: this.form.paradas.map((parada) => ({
						cidade_uf: parada.cidade_uf,
						tipo: parada.tipo,
						cliente: parada.cliente,
						observacoes: parada.observacoes || null,
					})),
				};

				const response = this.isEditMode
					? await viagensServices.atualizar(this.viagem.id, payload)
					: await viagensServices.criar(payload);

				this.$toasted?.show(this.isEditMode ? 'Viagem atualizada com sucesso' : 'Viagem cadastrada com sucesso');
				this.$emit('saved', response?.data || null);
				this.fecharDialog(true);
			} catch (error) {
				const message = error?.response?.data?.error || (this.isEditMode ? 'Erro ao atualizar viagem' : 'Erro ao cadastrar viagem');
				this.$toasted?.show(message);
			} finally {
				this.saving = false;
			}
		},
	},

	mounted() {
		this.buscarMunicipios();
	},
};
</script>

<style scoped>
.viagem-dialog {
	border-radius: 16px !important;
	border-color: #e6ebf1 !important;
}

.viagem-dialog__title {
	color: #1f2a44;
	font-weight: 800;
}

.viagem-section-title {
	color: #10b981;
	font-size: 16px;
	font-weight: 700;
}

.parada-box {
	border: 1px solid #e6ebf1;
	border-radius: 12px;
	padding: 12px 12px 4px;
	background: #fbfdff;
}

.parada-box__header {
	display: flex;
	align-items: center;
	justify-content: space-between;
	margin-bottom: 8px;
	color: #1f2a44;
}

.viagem-add-stop-btn {
	color: #10b981 !important;
	font-weight: 700;
	text-transform: none;
}

.viagem-save-btn {
	background-color: #10b981 !important;
	color: #ffffff !important;
	border-radius: 12px;
	text-transform: none;
	font-weight: 700;
	letter-spacing: 0.01em;
	padding: 10px 16px;
}
</style>
